import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback } from "react";
import {
	type CanvaStatus,
	type CategoryMappingsUpdateDto,
	type HeyGenStatus,
	type IdDto,
	type MasterLocationUpdateDto,
	type MyBusinessAvailableAttributesDto,
	type MyBusinessCategoriesListDto,
	type MyBusinessGetCategoryDto,
	type MyBusinessInfoUpdateDto,
	type MyBusinessLocationAttributesDto,
	type MyBusinessLocationDto,
	type MyBusinessLocationUpdateDto,
	type MyBusinessReviewReplyDto,
	type MyBusinessReviewsListDto,
	PlatformEnum,
	type PlatformProfile,
	type PlatformStatus,
	type TikTokAdsStatus,
} from "shared";
import { Api } from "../api.config";
import { useBillingEntitlements } from "../billing/useSubscription";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { QueryKeys } from "../LinkGen";
import { buildConnectedPlatforms } from "./connected-platforms";

/**
 * Social connection summary. Deliberately queries only the social platforms:
 * this runs on the calendar page, and every status call can trigger a token
 * refresh server-side, so pulling in WordPress and Google Business here made
 * those integrations refresh far more often than they need to.
 *
 * Use `useConnectedPlatforms` when you need the full per-platform set.
 */
export const useIntegrations = () => {
	const { status: statusFacebook } = useFacebook();
	const { status: statusInstagram } = useInstagram();
	const { status: statusThreads } = useThreads();
	const { status: statusLinkedin } = useLinkedin();
	const { status: statusTikTok } = useTikTok();
	const { status: statusYouTube } = useYouTube();
	const statusX = useX();

	return {
		atLeastOneConnected:
			statusFacebook.data.connected ||
			statusInstagram.data.connected ||
			statusThreads.data.connected ||
			statusLinkedin.data.connected ||
			statusTikTok.data.connected ||
			statusYouTube.data.connected ||
			statusX.connected,
	};
};

/**
 * Every platform's connection state, including the blog and Google Business
 * ones. Only call this from surfaces that actually need per-platform detail —
 * it issues a status request per platform, and each of those can trigger a
 * server-side token refresh.
 */
export const useConnectedPlatforms = () => {
	const { status: statusFacebook } = useFacebook();
	const { status: statusInstagram } = useInstagram();
	const { status: statusThreads } = useThreads();
	const { status: statusLinkedin } = useLinkedin();
	const { status: statusTikTok } = useTikTok();
	const { status: statusYouTube } = useYouTube();
	const { status: statusWordpress } = useWordpress();
	const { status: statusGoogleMyBusiness } = useGoogleMyBusiness();
	const statusX = useX();

	// `custom-blog` is the clipboard-copy flow: it has no connection to report.
	return buildConnectedPlatforms([
		{
			platform: PlatformEnum.FACEBOOK,
			connected: statusFacebook.data.connected,
		},
		{
			platform: PlatformEnum.INSTAGRAM,
			connected: statusInstagram.data.connected,
		},
		{ platform: PlatformEnum.THREADS, connected: statusThreads.data.connected },
		{
			platform: PlatformEnum.LINKEDIN,
			connected: statusLinkedin.data.connected,
		},
		{ platform: PlatformEnum.TIKTOK, connected: statusTikTok.data.connected },
		{ platform: PlatformEnum.YOUTUBE, connected: statusYouTube.data.connected },
		{ platform: PlatformEnum.X, connected: statusX.connected },
		{
			platform: PlatformEnum.WORDPRESS,
			connected: statusWordpress.data.connected,
		},
		{
			platform: PlatformEnum.GOOGLE_BUSINESS,
			connected: statusGoogleMyBusiness.data.connected,
		},
	]);
};

export const useFacebook = () => {
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Facebook.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			try {
				return await Api.GetFacebookStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useFacebookActions = () => {
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		window.location.href = await Api.FacebookConnect().then((res) => res.data);
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.FacebookDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Facebook.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Facebook.Profiles(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Facebook disconnect has been successful",
			});
		},
	});

	const getProfiles = useMutation({
		mutationFn: async () => {
			const profiles = await Api.GetFacebookProfiles();
			await queryClient.setQueryData(
				QueryKeys.Integrations.Facebook.Profiles(companyId),
				profiles,
			);
			return profiles.data;
		},
	});

	const setProfile = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.SetFacebookProfile(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Facebook.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Facebook.Profiles(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Facebook profile has been set",
			});
		},
	});

	return {
		connect,
		disconnect,
		setProfile,
		getProfiles,
	};
};

// duplicate to method in hook above
// reason: above hook is dependent on useCompany hook:
//  - cannot use useCompany when trying to  call connect and not logged in
export const useFacebookUnprotectedAction = () => {
	const connect = useCallback(async (inviteToken?: string) => {
		window.location.href = await Api.FacebookConnect(inviteToken).then(
			(res) => res.data,
		);
	}, []);

	return { connect };
};

export const useInstagram = () => {
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Instagram.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			try {
				return await Api.GetInstagramStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useInstagramActions = () => {
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		window.location.href = await Api.InstagramConnect().then((res) => res.data);
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.InstagramDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Instagram.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Instagram.Profiles(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Instagram disconnect has been successful",
			});
		},
	});

	const getProfiles = useMutation({
		mutationFn: async () => {
			const profiles = await Api.GetInstagramProfiles();
			await queryClient.setQueryData(
				QueryKeys.Integrations.Instagram.Profiles(companyId),
				profiles,
			);
			return profiles.data;
		},
	});

	const setProfile = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.SetInstagramProfile(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Instagram.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Instagram.Profiles(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Instagram profile has been set",
			});
		},
	});

	return {
		connect,
		disconnect,
		getProfiles,
		setProfile,
	};
};

export const useThreads = () => {
	const { id: companyId } = useCompany();

	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.ThreadsStatus(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			try {
				return await Api.GetThreadsStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useThreadsActions = () => {
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		window.location.href = await Api.ThreadsConnect().then((res) => res.data);
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.ThreadsDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.ThreadsStatus(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Threads disconnect has been successful",
			});
		},
	});

	return {
		connect,
		disconnect,
	};
};

export const useLinkedin = () => {
	const { id: companyId } = useCompany();

	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Linkedin.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			try {
				return await Api.LinkedinStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useLinkedinActions = () => {
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.LinkedinConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.LinkedinDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Linkedin.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Linkedin disconnect has been successful",
			});
		},
	});

	const getProfiles = useMutation({
		mutationFn: async () => {
			const profiles = await Api.GetLinkedinProfiles();
			await queryClient.setQueryData(
				QueryKeys.Integrations.Linkedin.Profiles(companyId),
				profiles,
			);
			return profiles.data;
		},
	});

	const setProfile = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.SetLinkedinProfile(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Linkedin.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Linkedin.Profiles(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Linkedin profile has been set",
			});
		},
	});

	return {
		connect,
		disconnect,
		getProfiles,
		setProfile,
	};
};

export const useGoogleProfileActions = () => {
	const connect = useCallback(async (inviteToken?: string) => {
		const link = await Api.GoogleProfileConnect(inviteToken).then(
			(res) => res.data,
		);
		window.location.href = link;
	}, []);

	return {
		connect,
	};
};

export const useWordpress = () => {
	const { id: companyId } = useCompany();

	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Wordpress.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			try {
				return await Api.WordpressStatus().then((res) => res.data);
			} catch (_) {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useWordpressActions = () => {
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	const connect = useMutation({
		mutationFn: async (website: string) => {
			return Api.WordpressConnect({
				website,
			}).then((res) => res.data);
		},
		onSuccess: async (response: string) => {
			window.location.href = response;
		},
		onError: (e: Error) => {
			if (isAxiosError(e) && e.response?.status === 422) {
				return;
			}

			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) ? e.response?.data?.message : undefined) ||
					"Failed to connect to WordPress",
			});
		},
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.WordpressDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Wordpress.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Wordpress disconnect has been successful",
			});
		},
	});

	return {
		connect,
		disconnect,
	};
};

export const useWix = () => {
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Wix.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			try {
				return await Api.WixStatus().then((res) => res.data);
			} catch (_) {
				emit({
					type: AlertType.ERROR,
					content: "Error retrieving Wix status",
				});
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useWixActions = () => {
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();

	/** End-customer path: redirect to Wix App Market install. */
	const connect = useMutation({
		mutationFn: async () => {
			return (await Api.WixConnect()).data;
		},
		onSuccess: (url: string) => {
			window.location.href = url;
		},
		onError: (e: Error) => {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) ? e.response?.data?.message : undefined) ||
					"Failed to start Wix connect. Is WIX_APP_ID configured?",
			});
		},
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.WixDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Wix.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Wix disconnected",
			});
		},
	});

	return { connect, disconnect };
};

export const useGoogleSearchConsole = () => {
	const isEnabled = useFeatureFlag("GoogleSearchConsoleIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.SearchConsole.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}

			try {
				return await Api.GoogleSearchConsoleStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useGoogleSearchConsoleActions = () => {
	const isEnabled = useFeatureFlag("GoogleSearchConsoleIntegration");
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.GoogleSearchConsoleConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.GoogleSearchConsoleDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.SearchConsole.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Google disconnect has been successful",
			});
		},
	});

	const getWebsite = useSuspenseQuery({
		queryKey:
			QueryKeys.Integrations.Google.SearchConsole.WebsitesSingle(companyId),
		queryFn: async (): Promise<PlatformProfile> => {
			if (!isEnabled) {
				return { id: "-1", name: "" };
			}

			try {
				return (await Api.GoogleSearchConsoleGetWebsite()).data;
			} catch (_) {
				return { id: "-1", name: "Set Website" };
			}
		},
	});

	const getWebsites = useMutation({
		mutationFn: async () => {
			const profiles = (await Api.GoogleSearchConsoleGetWebsites()).data;
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.SearchConsole.Status(companyId),
			});
			return profiles.length
				? profiles
				: [{ id: "-1", name: "No Websites found" }];
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data.message
						? `Failed to get websites: ${e.response?.data.message}`
						: "Failed to get websites",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: "Failed to get websites",
			});
		},
	});

	const setWebsite = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.GoogleSearchConsoleSetWebsite({
				url: dto.id,
			});
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.SearchConsole.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.SearchConsole.Websites(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.SearchConsole.WebsitesSingle(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Google Search Console profile has been set",
			});
		},
	});
	return { connect, disconnect, getWebsite, getWebsites, setWebsite };
};

export const useGoogleMyBusiness = () => {
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();
	const enabled = useFeatureFlag("GoogleMyBusinessIntegration");
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.MyBusiness.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!enabled) {
				return { connected: false };
			}

			try {
				const res = await Api.GoogleMyBusinessStatus().then((res) => res.data);

				if (res.message === "disconnected") {
					emit({
						type: AlertType.WARNING,
						content:
							"Google my business has been disconnected. Please reconnect from the apps page if you wish to continue using this feature.",
					});
				}

				return res;
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

/**
 * Ensures a master row exists for this Google location. Each location keeps
 * its own saved profile, keyed by the Google location id.
 */
export const useEnsureMasterLocation = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Locations.Master(companyId, googleLocationId),
		queryFn: async () => {
			return (await Api.LocationsEnsureMaster(googleLocationId)).data;
		},
		enabled: !!googleLocationId,
		retry: false,
		staleTime: 60_000,
	});
};

export const useMasterLocation = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Locations.Master(companyId, googleLocationId),
		queryFn: async () => {
			return (await Api.LocationsGetMaster(googleLocationId)).data;
		},
		enabled: !!googleLocationId,
		staleTime: 30_000,
	});
};

export const useMasterLocationActions = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const checkDrift = useMutation({
		mutationFn: async () => {
			return (await Api.LocationsCheckDrift(googleLocationId)).data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				QueryKeys.Locations.Master(companyId, googleLocationId),
				data.master,
			);
			if (data.status === "skipped") {
				emit({
					type: AlertType.WARNING,
					content: "No Google listing linked to check",
				});
				return;
			}
			emit({
				type:
					data.status === "drift" || data.status === "error"
						? AlertType.WARNING
						: AlertType.SUCCESS,
				content:
					data.status === "drift"
						? `Drift detected: ${data.driftedFields.join(", ") || "profile fields"}`
						: data.status === "error"
							? "Drift check failed"
							: "Listings match your master profile",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to check listing drift",
			});
		},
	});

	const publishEverywhere = useMutation({
		mutationFn: async () => {
			return (await Api.LocationsPublishEverywhere(googleLocationId)).data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				QueryKeys.Locations.Master(companyId, googleLocationId),
				data.master,
			);
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.History(companyId, googleLocationId),
			});
			const synced = data.results.filter((r) => r.status === "synced");
			const errors = data.results.filter((r) => r.status === "error");
			const skipped = data.results.filter((r) => r.status === "skipped");
			const site =
				data.websiteSchema?.status === "synced"
					? " · website schema updated"
					: data.websiteSchema?.status === "error"
						? " · website schema failed"
						: "";
			if (errors.length > 0) {
				emit({
					type: AlertType.WARNING,
					content: `Published with errors: ${errors.map((e) => e.platform).join(", ")}${site}`,
				});
			} else if (synced.length > 0) {
				emit({
					type: AlertType.SUCCESS,
					content: `Published to ${synced.map((s) => s.platform).join(", ")}${
						skipped.length ? ` (${skipped.length} skipped)` : ""
					}${site}`,
				});
			} else {
				emit({
					type: AlertType.WARNING,
					content: `No platforms were published${site}`,
				});
			}
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to publish profile",
			});
		},
	});

	const syncWebsiteSchema = useMutation({
		mutationFn: async () => {
			return (await Api.LocationsSyncWebsiteSchema(googleLocationId)).data;
		},
		onSuccess: (data) => {
			const targets = [
				data.wordpress?.status === "synced" ? "WordPress" : null,
				data.wix?.status === "synced" ? "Wix" : null,
			].filter(Boolean);
			if (data.status === "synced") {
				emit({
					type: AlertType.SUCCESS,
					content: `Website LocalBusiness schema updated on ${targets.join(" + ") || "connected site"}`,
				});
				return;
			}
			if (data.status === "skipped") {
				emit({
					type: AlertType.WARNING,
					content: data.error || "Website schema sync skipped",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: data.error || "Website schema sync failed",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to sync website schema",
			});
		},
	});

	return { checkDrift, publishEverywhere, syncWebsiteSchema };
};

export const useCategoryMappings = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	const master = useMasterLocation(googleLocationId);
	return useQuery({
		queryKey: QueryKeys.Locations.CategoryMappings(companyId, googleLocationId),
		queryFn: async () => {
			return (await Api.LocationsGetCategoryMappings(googleLocationId)).data;
		},
		enabled: !!googleLocationId && !!master.data,
		staleTime: 30_000,
	});
};

export const useCategoryMappingActions = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const updateMappings = useMutation({
		mutationFn: async (dto: CategoryMappingsUpdateDto) => {
			return (await Api.LocationsUpdateCategoryMappings(dto, googleLocationId))
				.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				QueryKeys.Locations.CategoryMappings(companyId, googleLocationId),
				data,
			);
			emit({
				type: AlertType.SUCCESS,
				content:
					data.unmappedCount > 0
						? `Category mapping saved (${data.unmappedCount} still unmapped)`
						: "Category mappings saved",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to save category mapping",
			});
		},
	});

	return { updateMappings };
};

export const useProfileHistory = (
	enabled = true,
	googleLocationId?: string,
) => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Locations.History(companyId, googleLocationId),
		queryFn: async () => {
			return (await Api.LocationsListHistory(50, googleLocationId)).data;
		},
		enabled: enabled && !!googleLocationId,
		staleTime: 15_000,
	});
};

export const useUnifiedReviews = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Locations.Reviews(companyId, googleLocationId),
		queryFn: async () => {
			return (await Api.LocationsListReviews(false, googleLocationId)).data;
		},
	});
	return data;
};

export const useUnifiedReviewsActions = (googleLocationId?: string) => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const refresh = useMutation({
		mutationFn: async () => {
			const data = (await Api.LocationsRefreshReviews(googleLocationId)).data;
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.Reviews(companyId, googleLocationId),
			});
			return data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to refresh reviews",
			});
		},
	});

	const reply = useMutation({
		mutationFn: async ({
			reviewId,
			comment,
		}: {
			reviewId: number;
			comment: string;
		}) => {
			await Api.LocationsReplyToReview(reviewId, { comment }, googleLocationId);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.Reviews(companyId, googleLocationId),
			});
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Reply posted.",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to post reply",
			});
		},
	});

	const generateReply = useMutation({
		mutationFn: async (reviewId: number) => {
			return (
				await Api.LocationsGenerateReviewReply(reviewId, googleLocationId)
			).data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to generate review reply",
			});
		},
	});

	return { refresh, reply, generateReply };
};

export const useReviewReplyRules = () => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Locations.ReviewReplyRules(companyId),
		queryFn: async () => {
			return (await Api.LocationsGetReviewReplyRules()).data;
		},
	});
};

export const useReviewReplyRulesActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const updateRules = useMutation({
		mutationFn: async (autoSendRatings: number[]) => {
			return (await Api.LocationsUpdateReviewReplyRules({ autoSendRatings }))
				.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				QueryKeys.Locations.ReviewReplyRules(companyId),
				data,
			);
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to save review reply rules",
			});
		},
	});

	return { updateRules };
};

export const useGoogleMyBusinessLocations = () => {
	const { id: companyId } = useCompany();
	const enabled = useFeatureFlag("GoogleMyBusinessIntegration");
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.MyBusiness.Locations(companyId),
		queryFn: async (): Promise<MyBusinessLocationDto[]> => {
			if (!enabled) {
				return [];
			}

			return (await Api.GoogleMyBusinessGetLocations()).data;
		},
	});

	return data;
};

/**
 * The GBP location selected for this company (persisted server-side), or null
 * when none has been picked yet.
 */
export const useGoogleMyBusinessSelectedLocation = () => {
	const { id: companyId } = useCompany();
	const enabled = useFeatureFlag("GoogleMyBusinessIntegration");
	const { data } = useSuspenseQuery({
		queryKey:
			QueryKeys.Integrations.Google.MyBusiness.SelectedLocation(companyId),
		queryFn: async (): Promise<PlatformProfile | null> => {
			if (!enabled) {
				return null;
			}

			return (await Api.GoogleMyBusinessGetSelectedLocation()).data;
		},
	});

	return data;
};

export const useGoogleMyBusinessLocation = (locationID: string) => {
	const { id: companyId } = useCompany();

	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingle(
			companyId,
			locationID,
		),
		queryFn: async (): Promise<MyBusinessLocationUpdateDto> => {
			return (await Api.GoogleMyBusinessGetLocation(locationID)).data;
		},
	});

	return data;
};

export const useGoogleMyBusinessLocationAvailableAttributes = (
	locationId: string,
) => {
	const { id: companyId } = useCompany();

	const { data } = useSuspenseQuery({
		queryKey:
			QueryKeys.Integrations.Google.MyBusiness.LocationsAvailableAttributes(
				companyId,
				locationId,
			),
		queryFn: async (): Promise<MyBusinessAvailableAttributesDto> => {
			return (
				await Api.GoogleMyBusinessGetLocationAvailableAttributes(locationId)
			).data;
		},
	});

	return data;
};
export const useGoogleMyBusinessLocationAttributes = (locationId: string) => {
	const { id: companyId } = useCompany();

	const { data } = useSuspenseQuery({
		queryKey:
			QueryKeys.Integrations.Google.MyBusiness.LocationsSingleAttributes(
				companyId,
				locationId,
			),
		queryFn: async (): Promise<MyBusinessLocationAttributesDto> => {
			return (await Api.GoogleMyBusinessGetLocationAttributes(locationId)).data;
		},
	});

	return data;
};

export const useGoogleMyBusinessCategories = () => {
	const { id: companyId } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.MyBusiness.Categories(companyId),
		queryFn: async (): Promise<MyBusinessCategoriesListDto> => {
			return (await Api.GoogleMyBusinessGetCategories("en", "US")).data;
		},
	});

	return data;
};

export const useGoogleMyBusinessReviews = (locationId: string) => {
	const { id: companyId } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingleReviews(
			companyId,
			locationId,
		),
		queryFn: async (): Promise<MyBusinessReviewsListDto> => {
			return (await Api.GoogleMyBusinessGetLocationReviews(locationId)).data;
		},
	});
	return data;
};

export const useGoogleMyBusinessActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const enabled = useFeatureFlag("GoogleMyBusinessIntegration");

	const connect = useCallback(async () => {
		const link = await Api.GoogleMyBusinessConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.GoogleMyBusinessDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Google My Business disconnect has been successful",
			});
		},
	});

	const getAccounts = useMutation({
		mutationFn: async () => {
			const accounts = (await Api.GoogleMyBusinessGetAccounts()).data;
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.AccountsAsProfiles(
						companyId,
					),
			});
			return accounts;
		},
	});

	const getAccount = useSuspenseQuery({
		queryKey:
			QueryKeys.Integrations.Google.MyBusiness.AccountsSingle(companyId),
		queryFn: async (): Promise<PlatformProfile | null> => {
			if (!enabled) {
				return null;
			}
			try {
				return (await Api.GoogleMyBusinessGetAccount()).data;
			} catch {
				return null;
			}
		},
	});

	const setAccount = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.GoogleMyBusinessSetAccount({
				id: dto.id,
			});
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.AccountsSingle(companyId),
			});

			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.Locations(companyId),
			});

			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.SelectedLocation(companyId),
			});
		},
	});

	const resyncAccount = useMutation({
		mutationFn: async () => {
			const accounts = (await Api.GoogleMyBusinessResyncAccount()).data;
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.AccountsSingle(companyId),
			});
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.AccountsAsProfiles(
						companyId,
					),
			});
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.Locations(companyId),
			});
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.SelectedLocation(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Google My Business account re-synced.",
			});
			return accounts;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to re-sync Google My Business account.",
			});
		},
	});

	const setSelectedLocation = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.GoogleMyBusinessSetSelectedLocation({ id: dto.id });
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.SelectedLocation(companyId),
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to save the selected Google Business Profile.",
			});
		},
	});

	const getCategories = useMutation({
		mutationFn: async (dto: MyBusinessGetCategoryDto) => {
			return (
				await Api.GoogleMyBusinessGetCategories(
					dto.languageCode,
					dto.regionCode,
					dto.view,
				)
			).data;
		},
	});

	const updateBusinessInfo = useMutation({
		mutationFn: async ({
			location,
			dto,
		}: {
			location: string;
			dto: MyBusinessInfoUpdateDto;
		}) => {
			await Api.GoogleMyBusinessUpdateBusinessInfo(location, dto);
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.AccountsSingle(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingle(
					companyId,
					location,
				),
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.LocationsSingleAttributes(
						companyId,
						location,
					),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.Master(companyId),
			});
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Business profile saved.",
			});
		},
	});

	/** Save the master profile only. Publish everywhere pushes platforms. */
	const updateMasterProfile = useMutation({
		mutationFn: async ({
			location,
			dto,
		}: {
			location: string;
			dto: MasterLocationUpdateDto;
		}) => {
			await Api.LocationsUpdateMaster({
				...dto,
				googleLocationId: location,
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.AccountsSingle(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingle(
					companyId,
					location,
				),
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.LocationsSingleAttributes(
						companyId,
						location,
					),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.Master(companyId, location),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.History(companyId, location),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.CategoryMappings(companyId, location),
			});
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content:
					"Saved to your master profile. Publish everywhere to update connected platforms.",
			});
		},
	});

	const uploadBusinessMedia = useMutation({
		mutationFn: async ({
			locationID,
			ids,
		}: {
			locationID: string;
			ids: Array<number>;
		}) => {
			const res = (
				await Api.GoogleMyBusinessUploadLocationMedia(locationID, ids)
			).data;

			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingleMedia(
					companyId,
					locationID,
				),
			});

			return res;
		},
	});

	const deleteBusinessMedia = useMutation({
		mutationFn: async ({
			locationID,
			mediaID,
		}: {
			locationID: string;
			mediaID: string;
		}) => {
			const res = (
				await Api.GoogleMyBusinessDeleteLocationMedia(locationID, mediaID)
			).data;

			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingleMedia(
					companyId,
					locationID,
				),
			});

			return res;
		},
	});

	const updateBusinessReviewReply = useMutation({
		mutationFn: async ({
			locationID,
			reviewID,
			dto,
		}: {
			locationID: string;
			reviewID: string;
			dto: MyBusinessReviewReplyDto;
		}) => {
			await Api.GoogleMyBusinessUpdateLocationReviewReply(
				locationID,
				reviewID,
				{
					comment: dto.comment,
				},
			);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingle(
					companyId,
					locationID,
				),
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.LocationsSingleReviews(
						companyId,
						locationID,
					),
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update review reply",
			});
		},
	});
	const deleteBusinessReviewReply = useMutation({
		mutationFn: async ({
			locationID,
			reviewID,
		}: {
			locationID: string;
			reviewID: string;
		}) => {
			await Api.GoogleMyBusinessDeleteLocationReviewReply(locationID, reviewID);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingle(
					companyId,
					locationID,
				),
			});
			await queryClient.refetchQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.LocationsSingleReviews(
						companyId,
						locationID,
					),
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to delete review reply",
			});
		},
	});

	const generateBusinessReviewReply = useMutation({
		mutationFn: async ({
			locationID,
			reviewID,
		}: {
			locationID: string;
			reviewID: string;
		}) => {
			return (
				await Api.GoogleMyBusinessGenerateLocationReviewReply(
					locationID,
					reviewID,
				)
			).data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to generate review reply",
			});
		},
	});

	const refreshLocation = useMutation({
		mutationFn: async (locationID: string) => {
			await Api.GoogleMyBusinessRefreshLocation(locationID);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingle(
					companyId,
					locationID,
				),
			});
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.LocationsSingleAttributes(
						companyId,
						locationID,
					),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Locations.Master(companyId, locationID),
			});
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Business profile refreshed from Google.",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to refresh business profile.",
			});
		},
	});

	const refreshReviews = useMutation({
		mutationFn: async (locationID: string) => {
			const res = (await Api.GoogleMyBusinessRefreshLocationReviews(locationID))
				.data;
			await queryClient.invalidateQueries({
				queryKey:
					QueryKeys.Integrations.Google.MyBusiness.LocationsSingleReviews(
						companyId,
						locationID,
					),
			});
			return res;
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Reviews refreshed from Google.",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to refresh reviews.",
			});
		},
	});

	return {
		connect,
		disconnect,
		getAccounts,
		getAccount,
		setAccount,
		resyncAccount,
		setSelectedLocation,
		getCategories,
		updateBusinessInfo,
		updateMasterProfile,
		updateBusinessReviewReply,
		deleteBusinessReviewReply,
		generateBusinessReviewReply,
		uploadBusinessMedia,
		deleteBusinessMedia,
		refreshLocation,
		refreshReviews,
	};
};

export const useGoogleMyBusinessLocationsMedia = (locationID: string) => {
	const { id: companyId } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.MyBusiness.LocationsSingleMedia(
			companyId,
			locationID,
		),
		queryFn: async () => {
			return (await Api.GoogleMyBusinessGetLocationMedia(locationID)).data;
		},
	});
	return data;
};

export const useGoogleAnalytics = () => {
	const isEnabled = useFeatureFlag("GoogleAnalyticsIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.Analytics.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}

			try {
				return await Api.GoogleAnalyticsStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useGoogleAnalyticsActions = () => {
	const isEnabled = useFeatureFlag("GoogleAnalyticsIntegration");
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.GoogleAnalyticsConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.GoogleAnalyticsDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.Analytics.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Google Analytics disconnect has been successful",
			});
		},
	});

	const getProperty = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Google.Analytics.Property(companyId),
		queryFn: async (): Promise<PlatformProfile> => {
			if (!isEnabled) {
				return { id: "-1", name: "" };
			}

			try {
				return (await Api.GoogleAnalyticsGetProperty()).data;
			} catch (_) {
				return { id: "-1", name: "Set Property" };
			}
		},
	});

	const getProperties = useMutation({
		mutationFn: async () => {
			const profiles = (await Api.GoogleAnalyticsGetProperties()).data;
			return profiles.length
				? profiles
				: [{ id: "-1", name: "No properties found" }];
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data.message
						? `Failed to get properties: ${e.response?.data.message}`
						: "Failed to get properties",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: "Failed to get properties",
			});
		},
	});

	const setProperty = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.GoogleAnalyticsSetProperty({ id: dto.id });
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.Google.Analytics.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.Analytics.Property(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Google.Analytics.Traffic(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Google Analytics property has been set",
			});
		},
	});

	return { connect, disconnect, getProperty, getProperties, setProperty };
};

export const useMetaAds = () => {
	const isEnabled = useFeatureFlag("MetaAdsIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}

			try {
				return await Api.GetMetaAdsStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useMetaAdsActions = () => {
	const isEnabled = useFeatureFlag("MetaAdsIntegration");
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.MetaAdsConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.MetaAdsDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Meta Ads disconnect has been successful",
			});
		},
	});

	const getAdAccount = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.AdAccountSingle(companyId),
		queryFn: async (): Promise<PlatformProfile | null> => {
			if (!isEnabled) {
				return null;
			}

			try {
				return (await Api.GetMetaAdsAdAccount()).data;
			} catch (_) {
				return null;
			}
		},
	});

	const getAdAccounts = useMutation({
		mutationFn: async () => {
			const accounts = (await Api.GetMetaAdsAdAccounts()).data;
			return accounts.length
				? accounts
				: [{ id: "-1", name: "No ad accounts found" }];
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data.message
						? `Failed to get ad accounts: ${e.response?.data.message}`
						: "Failed to get ad accounts",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: "Failed to get ad accounts",
			});
		},
	});

	const setAdAccount = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.SetMetaAdsAdAccount({ id: dto.id });
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.AdAccountSingle(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
				],
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Meta Ads ad account has been set",
			});
		},
	});

	const getPage = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.PageSingle(companyId),
		queryFn: async (): Promise<PlatformProfile | null> => {
			if (!isEnabled) {
				return null;
			}

			try {
				return (await Api.GetMetaAdsPage()).data;
			} catch (_) {
				return null;
			}
		},
	});

	const getPages = useMutation({
		mutationFn: async () => {
			const pages = (await Api.GetMetaAdsPages()).data;
			return pages.length
				? pages
				: [{ id: "-1", name: "No Facebook Pages found" }];
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data.message
						? `Failed to get Facebook Pages: ${e.response?.data.message}`
						: "Failed to get Facebook Pages",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: "Failed to get Facebook Pages",
			});
		},
	});

	const setPage = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.SetMetaAdsPage(dto.id);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.PageSingle(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Facebook Page has been set",
			});
		},
	});

	return {
		connect,
		disconnect,
		getAdAccount,
		getAdAccounts,
		setAdAccount,
		getPage,
		getPages,
		setPage,
	};
};

export const useYouTube = () => {
	const isEnabled = useFeatureFlag("YouTubeIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.YouTube.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}
			try {
				return await Api.YouTubeStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useYouTubeActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.YouTubeConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const connectAnother = useCallback(async () => {
		const link = await Api.YouTubeConnect(true).then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.YouTubeDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.YouTube.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.YouTube.Channels(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "YouTube disconnect has been successful",
			});
		},
	});

	const disconnectChannel = useMutation({
		mutationFn: async (channelId: string) => {
			await Api.YouTubeDisconnect(channelId);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.YouTube.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.YouTube.Channels(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "YouTube channel has been removed",
			});
		},
	});

	const getChannels = useMutation({
		mutationFn: async () => {
			const channels = (await Api.YouTubeGetChannels()).data;
			return channels.length
				? channels
				: [{ id: "-1", name: "No channels found" }];
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to get YouTube channels",
			});
		},
	});

	const setChannel = useMutation({
		mutationFn: async (dto: IdDto) => {
			await Api.YouTubeSetChannel(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.YouTube.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.YouTube.Channels(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "YouTube channel has been set",
			});
		},
	});

	return {
		connect,
		connectAnother,
		disconnect,
		disconnectChannel,
		getChannels,
		setChannel,
	};
};

export const useTikTok = () => {
	const isEnabled = useFeatureFlag("TikTokIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.TikTok.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}
			try {
				return await Api.TikTokStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

export const useTikTokActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.TikTokConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.TikTokDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.TikTok.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Report(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.ReportTopPosts(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "TikTok disconnect has been successful",
			});
		},
	});

	return {
		connect,
		disconnect,
	};
};

export const useTikTokAds = () => {
	const isEnabled = useFeatureFlag("TikTokAdsIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.TikTokAds.Status(companyId),
		queryFn: async (): Promise<TikTokAdsStatus> => {
			if (!isEnabled) {
				return { connected: false, status: "disconnected" };
			}
			try {
				return await Api.GetTikTokAdsStatus().then((res) => res.data);
			} catch {
				return { connected: false, status: "disconnected" };
			}
		},
	});

	return { status };
};

export const useTikTokAdsActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.TikTokAdsConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const getAdvertisers = useCallback(
		() => Api.GetTikTokAdsAdvertisers().then((res) => res.data),
		[],
	);

	const setAdvertiser = useMutation({
		mutationFn: async (advertiserId: string) => {
			await Api.SetTikTokAdsAdvertiser({ advertiserId });
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.TikTokAds.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "TikTok advertiser account updated",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Could not update the TikTok advertiser account",
			});
		},
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.TikTokAdsDisconnect();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Integrations.TikTokAds.Status(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversations(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "TikTok Ads & Comments disconnect has been successful",
			});
		},
	});

	return {
		connect,
		getAdvertisers,
		setAdvertiser,
		disconnect,
	};
};

export const useCanva = () => {
	const isEnabled = useFeatureFlag("CanvaIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.Canva.Status(companyId),
		queryFn: async (): Promise<CanvaStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}
			try {
				return await Api.CanvaStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});

	return { status };
};

/** Non-suspense variant for pages outside a Suspense boundary. */
export const useCanvaQuery = () => {
	const isEnabled = useFeatureFlag("CanvaIntegration");
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Integrations.Canva.Status(companyId),
		queryFn: async (): Promise<CanvaStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}
			try {
				return await Api.CanvaStatus().then((res) => res.data);
			} catch {
				return { connected: false };
			}
		},
	});
};

export const useCanvaActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const connect = useCallback(async () => {
		const link = await Api.CanvaConnect().then((res) => res.data);
		window.location.href = link;
	}, []);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.CanvaDisconnect();
		},
		onSuccess: async () => {
			queryClient.setQueryData(QueryKeys.Integrations.Canva.Status(companyId), {
				connected: false,
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.Canva.Status(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Canva has been disconnected",
			});
		},
	});

	return {
		connect,
		disconnect,
	};
};

const fetchHeyGenStatus = async (enabled: boolean): Promise<HeyGenStatus> => {
	if (!enabled) {
		return { connected: false };
	}
	try {
		return (await Api.HeyGenStatus()).data;
	} catch {
		return { connected: false };
	}
};

export const useHeyGen = () => {
	const isEnabled = useFeatureFlag("HeyGenIntegration");
	const { id: companyId } = useCompany();
	const status = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.HeyGen.Status(companyId),
		queryFn: () => fetchHeyGenStatus(isEnabled),
	});

	return { status };
};

/** Non-suspense variant for pages outside a Suspense boundary. */
export const useHeyGenQuery = () => {
	const isEnabled = useFeatureFlag("HeyGenIntegration");
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Integrations.HeyGen.Status(companyId),
		queryFn: () => fetchHeyGenStatus(isEnabled),
	});
};

export const useHeyGenActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	// The key only exists inside this mutation's variables; nothing caches it.
	const connect = useMutation({
		mutationFn: async (apiKey: string) =>
			(await Api.HeyGenConnect({ apiKey })).data,
		onSuccess: (status) => {
			queryClient.setQueryData(
				QueryKeys.Integrations.HeyGen.Status(companyId),
				status,
			);
			emit({ type: AlertType.SUCCESS, content: "HeyGen connected" });
		},
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.HeyGenDisconnect();
		},
		onSuccess: async () => {
			queryClient.setQueryData(
				QueryKeys.Integrations.HeyGen.Status(companyId),
				{ connected: false },
			);
			emit({
				type: AlertType.SUCCESS,
				content:
					"HeyGen disconnected. Videos you already imported stay in your media library.",
			});
		},
	});

	return { connect, disconnect };
};

export const useGooglePlaceAutocomplete = (input: string, enabled = false) => {
	const { id: companyId } = useCompany();
	return useQuery({
		enabled,
		queryKey: QueryKeys.Integrations.Google.Places.Autocomplete(
			companyId,
			input,
		),
		queryFn: async () => {
			return (await Api.GooglePlacesAutocomplete(input)).data;
		},
	});
};

export const useX = () => {
	const isEnabled = useFeatureFlag("XIntegration");
	const { id: companyId } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.X.Status(companyId),
		queryFn: async (): Promise<PlatformStatus> => {
			if (!isEnabled) {
				return { connected: false };
			}
			try {
				return (await Api.XStatus()).data;
			} catch {
				return { connected: false };
			}
		},
	});
	return data;
};

export const useXActions = () => {
	const queryClient = useQueryClient();
	const company = useCompany();
	const emit = useAlertEmit();
	const { data: entitlements } = useBillingEntitlements();

	const connect = useCallback(async () => {
		if (!entitlements.xIntegration) {
			emit({
				type: AlertType.WARNING,
				content:
					"Activate the X Integration add-on in Billing first, then try to connect.",
			});
			return;
		}
		try {
			const link = (await Api.XConnect()).data;
			window.location.href = link;
		} catch (e: unknown) {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) && e.response?.data?.message) ||
					"Failed to start X connect",
			});
		}
	}, [emit, entitlements.xIntegration]);

	const disconnect = useMutation({
		mutationFn: async () => {
			await Api.XDisconnect();
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.X.Status(company.id),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Report(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "X disconnect has been successful",
			});
		},
	});

	return {
		connect,
		disconnect,
	};
};
